import { useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Stack, Switch, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import OccupantPicker from "../OccupantPicker";
import PhotoInput from "../PhotoInput";
import { useLanguage } from "../../i18n/LanguageContext";

export default function VisitDialog({ open, onClose, onSave }) {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [isProvider, setIsProvider] = useState(false);
  const [contact, setContact] = useState(null);
  const [reason, setReason] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setName(""); setIsProvider(false); setContact(null); setReason(""); setPhotos([]); setError("");
  };

  const handleSubmit = async () => {
    if (!name.trim()) return setError(t("visitantes.errNoName"));
    if (!isProvider && !contact) return setError(t("visitantes.errNoContact"));
    if (!reason.trim()) return setError(t("visitantes.errNoReason"));
    setSaving(true);
    setError("");
    try {
      await onSave({ name: name.trim(), isProvider, contactId: isProvider ? null : contact._id, reason: reason.trim(), photos });
      reset();
    } catch (err) {
      setError(err.response?.data?.error || t("visitantes.errGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {t("visitantes.registerVisit")}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label={t("visitantes.visitorName")} value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth />

          <FormControlLabel
            control={<Switch checked={isProvider} onChange={(e) => { setIsProvider(e.target.checked); if (e.target.checked) setContact(null); }} />}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LocalShippingIcon fontSize="small" /> <span>{t("visitantes.isProvider")}</span>
              </Stack>
            }
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {t("visitantes.contactLabel")} {!isProvider && `(${t("common.required")})`}
            </Typography>
            {isProvider ? (
              <Alert severity="info" icon={false}>{t("visitantes.contactNotApplicable")}</Alert>
            ) : (
              <OccupantPicker value={contact} onChange={setContact} />
            )}
          </Box>

          <TextField
            label={`${t("visitantes.reasonLabel")} (${t("common.required")})`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isProvider ? t("visitantes.reasonPlaceholderProvider") : t("visitantes.reasonPlaceholderVisit")}
            multiline
            minRows={2}
            fullWidth
          />

          <PhotoInput value={photos} onChange={setPhotos} max={6} label={`${t("visitantes.photosLabel")} (${t("common.optional")})`} />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button variant="contained" startIcon={<CheckIcon />} onClick={handleSubmit} disabled={saving}>
          {saving ? t("common.saving") : t("visitantes.registerEntry")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
